// =============================================================================
// Module Project - Hook: useAttachmentsStore
// Store pour les pièces jointes
// =============================================================================

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  fetchTaskAttachments,
  uploadAttachment,
  deleteAttachment,
} from '../data/attachmentRepository';
import { createActivityLog } from '../data/activityRepository';
import { notifyAttachmentUploaded } from '../data/notificationService';
import type { TaskAttachment } from '../types';

export function useAttachmentsStore(orgId: string | undefined) {
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD ATTACHMENTS FOR TASK
  // ─────────────────────────────────────────────────────────────────────────
  const loadAttachments = useCallback(async (taskId: string) => {
    setLoading(true);
    const data = await fetchTaskAttachments(taskId);
    setAttachments(data);
    setLoading(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // UPLOAD ATTACHMENT
  // ─────────────────────────────────────────────────────────────────────────
  const upload = useCallback(async (
    projectId: string,
    taskId: string,
    file: File,
    userId?: string,
    taskTitle?: string
  ): Promise<TaskAttachment | null> => {
    if (!orgId) return null;

    setUploading(true);
    try {
      const attachment = await uploadAttachment(orgId, projectId, taskId, file, userId);
      setUploading(false);

      if (attachment) {
        setAttachments(prev => [attachment, ...prev]);
        await createActivityLog(
          orgId,
          taskId,
          userId || null,
          'attachment_added',
          'attachment',
          null,
          file.name
        );
        
        // Send notifications to watchers, assignees and owner
        await notifyAttachmentUploaded({
          orgId,
          projectId,
          taskId,
          taskTitle: taskTitle || 'Tâche',
          attachmentId: attachment.id,
          fileName: file.name,
          actorUserId: userId,
        });
      }

      return attachment;
    } catch (error: any) {
      setUploading(false);
      console.error('Upload failed:', error);
      toast.error(`Échec du téléversement : ${error.message || 'Erreur inconnue'}`);
      return null;
    }
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // DELETE ATTACHMENT
  // ─────────────────────────────────────────────────────────────────────────
  const remove = useCallback(async (
    attachmentId: string,
    taskId: string,
    userId?: string
  ): Promise<boolean> => {
    if (!orgId) return false;

    const attachment = attachments.find(a => a.id === attachmentId);
    const success = await deleteAttachment(attachmentId);

    if (success) {
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      if (attachment) {
        await createActivityLog(
          orgId,
          taskId,
          userId || null,
          'attachment_removed',
          'attachment',
          attachment.file_name,
          null
        );
      }
    }

    return success;
  }, [orgId, attachments]);

  // ─────────────────────────────────────────────────────────────────────────
  // CLEAR
  // ─────────────────────────────────────────────────────────────────────────
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    attachments,
    loading,
    uploading,
    loadAttachments,
    upload,
    remove,
    clearAttachments,
  };
}
